import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';

interface DiscoveredItem {
  id: string;
  title: string;
  source: string;
  source_url: string;
  published_date: string;
  summary: string;
  content: string;
  content_preview: string;
  content_length: number;
  content_quality: 'full' | 'partial' | 'thin' | 'headline_only';
  relevance_score: number;
  source_count: number;
  all_sources: { name: string; url: string }[];
  image_url: string;
  discovered_at: string;
}

const qualityConfig = {
  full:         { label: 'Full Content',  color: 'bg-emerald-100 text-emerald-700', icon: 'check_circle' },
  partial:      { label: 'Partial',       color: 'bg-amber-100 text-amber-700',     icon: 'warning' },
  thin:         { label: 'Thin',          color: 'bg-orange-100 text-orange-700',   icon: 'error_outline' },
  headline_only:{ label: 'Headline Only', color: 'bg-red-100 text-red-700',         icon: 'dangerous' },
};

const NewsDiscovery: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems]                   = useState<DiscoveredItem[]>([]);
  const [selected, setSelected]             = useState<Set<string>>(new Set());
  const [expanded, setExpanded]             = useState<Set<string>>(new Set());
  const [discovering, setDiscovering]       = useState(false);
  const [generating, setGenerating]         = useState(false);
  const [error, setError]                   = useState('');
  const [genResult, setGenResult]           = useState<any>(null);
  const [discoveryStarted, setDiscoveryStarted] = useState('');
  const [totalSoFar, setTotalSoFar]         = useState(0);
  const [isDone, setIsDone]                 = useState(false);
  const [isFromStorage, setIsFromStorage]   = useState(false);
  const eventSourceRef                      = useRef<EventSource | null>(null);
  const [loadingStored, setLoadingStored]   = useState(true);

  useEffect(() => {
    const loadStored = async () => {
      try {
        const data = await apiService.getDiscoveredNews();
        if (data.items && data.items.length > 0) {
          setItems(data.items);
          setIsDone(true);
          setIsFromStorage(true);
        }
      } catch {
        // silently ignore — empty state will show
      } finally {
        setLoadingStored(false);
      }
    };
    loadStored();
  }, []);

  const handleDiscover = async () => {
    setDiscovering(true);
    setError('');
    setItems([]);
    setSelected(new Set());
    setGenResult(null);
    setTotalSoFar(0);
    setIsDone(false);
    setIsFromStorage(false);
    setDiscoveryStarted(new Date().toISOString());
    if (eventSourceRef.current) eventSourceRef.current.close();

    const es = apiService.discoverNewsStream(
      (batchItems, total) => {
        setItems(prev => {
          const existingIds = new Set(prev.map(i => i.id));
          const newItems = batchItems.filter((i: DiscoveredItem) => !existingIds.has(i.id));
          return [...prev, ...newItems];
        });
        setTotalSoFar(total);
      },
      (total) => { setDiscovering(false); setIsDone(true); setTotalSoFar(total); },
      (errMsg) => { setError(errMsg); setDiscovering(false); },
    );
    eventSourceRef.current = es;
  };

  const handleStopDiscovery = () => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setDiscovering(false);
    setIsDone(true);
  };

  const handleGenerate = async () => {
    if (selected.size === 0) return;
    setGenerating(true);
    setError('');
    setGenResult(null);
    try {
      const data = await apiService.generateSelected(Array.from(selected));
      setGenResult(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(selected.size === items.length ? new Set() : new Set(items.map(i => i.id)));
  };

  const handleDeleteItem = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await apiService.deleteDiscoveredNewsItem(id);
      setItems(prev => prev.filter(i => i.id !== id));
      setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
    } catch {
      setError('Failed to delete item.');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Remove all discovered news items?')) return;
    try {
      await apiService.clearDiscoveredNews();
      setItems([]);
      setSelected(new Set());
      setIsDone(false);
      setIsFromStorage(false);
    } catch {
      setError('Failed to clear news items.');
    }
  };

  const selectByQuality = (quality: string) => {
    const ids = items
      .filter(i => i.content_quality === quality ||
        (quality === 'good' && (i.content_quality === 'full' || i.content_quality === 'partial')))
      .map(i => i.id);
    setSelected(new Set(ids));
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    } catch { return dateString; }
  };

  const fullCount    = items.filter(i => i.content_quality === 'full').length;
  const partialCount = items.filter(i => i.content_quality === 'partial').length;
  const thinCount    = items.filter(i => i.content_quality === 'thin' || i.content_quality === 'headline_only').length;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-headline-lg text-on-surface">News Discovery</h1>
          <p className="text-on-surface-variant font-body text-body-md mt-1">
            Discover news, review extracted content, select stories for article generation
          </p>
          {discoveryStarted && (
            <p className="text-on-surface-variant font-mono text-label-xs mt-1">
              Discovery started: {formatDateTime(discoveryStarted)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {discovering && (
            <button
              onClick={handleStopDiscovery}
              className="flex items-center gap-2 px-5 py-3 bg-red-600 text-white rounded-xl font-display text-label-lg hover:bg-red-700 transition-all shadow-elevated"
            >
              <span className="material-symbols-outlined text-[20px]">stop_circle</span>
              Stop
            </button>
          )}
          <button
            onClick={handleDiscover}
            disabled={discovering}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-on-primary rounded-xl font-display text-label-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-elevated"
          >
            {discovering ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Discovering... ({totalSoFar} found)
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[20px]">travel_explore</span>
                Discover News
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 font-body text-body-md">
          {error}
        </div>
      )}

      {/* Live streaming indicator */}
      {discovering && items.length > 0 && (
        <div className="rounded-xl p-4 flex items-center gap-3"
          style={{ background: 'rgba(79,70,229,0.07)', border: '1px solid rgba(79,70,229,0.25)' }}>
          <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: '#4f46e5' }} />
          <span className="font-display text-label-md" style={{ color: '#92400e' }}>
            Live: {items.length} news items discovered so far, searching for more...
          </span>
        </div>
      )}

      {/* Stats bar */}
      {items.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="font-display text-headline-md text-on-surface">{items.length}</div>
              <div className="text-label-xs text-on-surface-variant uppercase tracking-wider">Discovered</div>
            </div>
            <div className="w-px h-10 bg-slate-200" />
            <div className="flex gap-3">
              <button onClick={() => selectByQuality('good')}
                className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-label-sm font-medium hover:bg-emerald-100 transition-colors">
                {fullCount} Full
              </button>
              <span className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-label-sm font-medium">
                {partialCount} Partial
              </span>
              <span className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-label-sm font-medium">
                {thinCount} Thin
              </span>
            </div>
            {isDone && (
              <>
                <div className="w-px h-10 bg-slate-200" />
                <div className="flex items-center gap-2 text-emerald-600">
                  <span className="material-symbols-outlined text-[18px]">
                    {isFromStorage ? 'inventory_2' : 'check_circle'}
                  </span>
                  <span className="text-label-sm font-medium">
                    {isFromStorage ? 'Loaded from previous session' : 'Discovery complete'}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-label-sm text-on-surface-variant font-medium">
              {selected.size} selected
            </span>
            <button
              onClick={handleClearAll}
              disabled={items.length === 0}
              title="Remove all discovered items"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-500 text-label-sm font-medium hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]">delete_sweep</span>
              Clear All
            </button>
            <button
              onClick={handleGenerate}
              disabled={selected.size === 0 || generating}
              className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg font-display text-label-md disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              style={{ background: selected.size > 0 && !generating ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)' : '#94a3b8', boxShadow: selected.size > 0 && !generating ? '0 3px 12px rgba(79,70,229,0.3)' : 'none' }}
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                  Generate {selected.size > 0 ? `(${selected.size})` : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Generation result */}
      {genResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-emerald-600">check_circle</span>
            <span className="font-display text-label-lg text-emerald-800">
              {genResult.articles_generated} article(s) generated in {genResult.total_steps} steps
            </span>
          </div>
          <div className="space-y-1">
            {(genResult.articles || []).map((a: any) => (
              <div key={a.id} className="text-body-sm text-emerald-700">
                <span className="font-medium">{a.headline}</span>
                <span className="text-emerald-500 ml-2">Style: {a.style_match_score}/100</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/content')}
            className="inline-flex items-center gap-1.5 mt-3 font-body text-body-sm font-medium transition-colors"
            style={{ color: '#4f46e5' }}
          >
            View in Articles
            <span className="material-symbols-outlined text-[15px]">arrow_forward</span>
          </button>
        </div>
      )}

      {/* Table */}
      {items.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[40px_1fr_180px_100px_90px_80px_70px_40px] gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200 text-label-xs uppercase tracking-wider text-on-surface-variant font-mono">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selected.size === items.length && items.length > 0}
                onChange={selectAll}
                className="w-4 h-4 rounded border-slate-300"
                style={{ accentColor: '#4f46e5' }}
              />
            </div>
            <div>Title & Source</div>
            <div>Date & Time</div>
            <div>Content</div>
            <div>Quality</div>
            <div>Score</div>
            <div>Sources</div>
            <div></div>
          </div>

          <div className="divide-y divide-slate-100">
            {items.map(item => {
              const qc = qualityConfig[item.content_quality];
              const isExpanded = expanded.has(item.id);
              const isSelected = selected.has(item.id);

              return (
                <div key={item.id}
                  className="transition-colors"
                  style={{ background: isSelected ? 'rgba(232,136,42,0.04)' : undefined }}>
                  <div
                    className="grid grid-cols-[40px_1fr_180px_100px_90px_80px_70px_40px] gap-2 px-4 py-3 items-center cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleExpand(item.id)}
                  >
                    <div className="flex items-center" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(item.id)}
                        className="w-4 h-4 rounded border-slate-300"
                        style={{ accentColor: '#4f46e5' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="font-body text-body-sm text-on-surface truncate font-medium">{item.title}</div>
                      <div className="font-body text-label-xs text-on-surface-variant truncate mt-0.5">
                        {item.source}
                        {item.source_url && (
                          <a
                            href={item.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="ml-2 transition-colors"
                            style={{ color: '#4f46e5' }}
                          >
                            <span className="material-symbols-outlined text-[14px] align-middle">open_in_new</span>
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="font-mono text-label-xs text-on-surface-variant">
                      <div>{formatDateTime(item.published_date)}</div>
                      {item.discovered_at && (
                        <div className="text-slate-400 mt-0.5" title="Discovered at">
                          Found: {formatDateTime(item.discovered_at)}
                        </div>
                      )}
                    </div>
                    <div className="font-mono text-label-xs text-on-surface-variant">
                      {item.content_length >= 1000
                        ? `${(item.content_length / 1000).toFixed(1)}k`
                        : item.content_length} chars
                    </div>
                    <div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-label-xs font-medium ${qc.color}`}>
                        <span className="material-symbols-outlined text-[14px]">{qc.icon}</span>
                        {qc.label}
                      </span>
                    </div>
                    <div className="font-mono text-label-sm text-on-surface">{item.relevance_score}</div>
                    <div className="font-mono text-label-sm text-on-surface">
                      {item.source_count > 1
                        ? <span className="text-emerald-600 font-medium">{item.source_count}</span>
                        : '1'}
                    </div>
                    <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={e => handleDeleteItem(e, item.id)}
                        title="Remove this item"
                        className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                    </div>
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 ml-10 mr-4">
                      <div className="rounded-lg p-4 border border-slate-200" style={{ background: '#f8fafc' }}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="material-symbols-outlined text-[16px] text-on-surface-variant">description</span>
                          <span className="font-display text-label-sm text-on-surface">Full Extracted Content</span>
                          <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-label-xs font-medium ${qc.color}`}>
                            {qc.label} ({item.content_length} chars)
                          </span>
                        </div>

                        <div className="flex gap-6 mb-3 text-label-xs font-mono text-slate-500">
                          <div><span className="text-slate-400">Published:</span> {formatDateTime(item.published_date)}</div>
                          {item.discovered_at && (
                            <div><span className="text-slate-400">Discovered:</span> {formatDateTime(item.discovered_at)}</div>
                          )}
                        </div>

                        <div className="bg-white rounded-md border border-slate-200 p-4 max-h-96 overflow-y-auto">
                          <p className="font-body text-body-sm text-on-surface whitespace-pre-wrap leading-relaxed">
                            {item.content || item.content_preview || item.summary || 'No content extracted'}
                          </p>
                        </div>

                        {item.source_url && (
                          <div className="mt-3 pt-2 border-t border-slate-200">
                            <span className="font-display text-label-xs text-slate-500 uppercase tracking-wider">Source URL:</span>
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-label-xs truncate mt-1 transition-colors"
                              style={{ color: '#4f46e5' }}
                            >
                              {item.source_url}
                            </a>
                          </div>
                        )}

                        {item.all_sources.length > 1 && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <span className="font-display text-label-xs text-on-surface-variant uppercase tracking-wider">
                              All Sources ({item.all_sources.length}):
                            </span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {item.all_sources.map((s, i) => (
                                <a
                                  key={i}
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-label-xs px-2 py-0.5 rounded transition-colors"
                                  style={{ color: '#4f46e5', background: 'rgba(79,70,229,0.08)' }}
                                >
                                  {s.name}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Loading stored */}
      {loadingStored && items.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-8 h-8 border-2 border-slate-200 rounded-full animate-spin mx-auto mb-4"
            style={{ borderTopColor: '#4f46e5' }} />
          <p className="text-on-surface-variant font-body text-body-md">Loading previously discovered news...</p>
        </div>
      )}

      {/* Empty state */}
      {!discovering && !loadingStored && items.length === 0 && !error && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <span className="material-symbols-outlined text-[48px] text-slate-300 mb-4 block">travel_explore</span>
          <h3 className="font-display text-headline-sm text-on-surface mb-2">No news discovered yet</h3>
          <p className="text-on-surface-variant font-body text-body-md max-w-md mx-auto">
            Click "Discover News" to search RSS feeds, NewsAPI, GNews, and company press releases for aluminium industry news.
            Results stream in batches as they are found.
          </p>
        </div>
      )}
    </div>
  );
};

export default NewsDiscovery;
